import { StudyWorkflowModules } from 'interfaces/study';

import { getStudyWorkflowConfiguration } from './studyWorkflowConfiguration';
import { StudyWorkflowStepDefinition } from './studyWorkflowStepTypes';

// All multiplex/mIF-only workflow content lives in this file. studyWorkflowModel.ts must stay
// modality-agnostic; it only decides whether/where to splice this content in for a multiplex study.

export const multiplexCaseImageAnalysisDescription =
  'Analyze each case image to extract tissue and cell data layers. Tissue segmentation follows the image modality; DAPI normalization and cell segmentation apply to multiplex images.';

// Setting and approving marker names is a prerequisite for every marker-dependent multiplex step
// below, so it is always prepended first whenever any of those steps is configured. It has no
// `module` gate: it is not itself one of the settings.workflow.modules toggles.
export const markerAssignmentsWorkflowStep: StudyWorkflowStepDefinition = {
  id: 'marker-assignments',
  label: 'Define multiplex panels',
  description:
    'Review channel settings, select matching slides, and save and approve each panel before running marker-dependent analyses.',
  category: 'multiplex',
};

export const markerNormalizationWorkflowStep: StudyWorkflowStepDefinition = {
  id: 'marker-normalization',
  label: 'Set normalization values',
  description:
    'Review panel ranges, set RNA display ranges, or run automatic normalization for selected markers and slides.',
  category: 'multiplex',
};

// Marker positivity and cell rules-based cell typing remain two independent settings.workflow.modules
// flags (with cell rules-based cell typing depending on marker positivity, enforced by
// getStudyWorkflowConfiguration). They are combined into one step here only for this coarse step
// list, since cell rules-based cell typing always implies marker positivity is enabled.
export const multiplexWorkflowSteps: StudyWorkflowStepDefinition[] = [
  {
    id: 'rna-probes',
    label: 'RNA probes spot detection',
    description: 'Run RNA probes spot detection on the multiplex slides in this cohort, then review the detections.',
    category: 'multiplex',
    module: 'rnaProbes',
  },
  {
    id: 'analysis-areas',
    label: 'Include and Exclude areas',
    description:
      'Draw Include within the tissue mask to limit the analysis area. Without Include, the tissue mask is used. Exclude areas are then removed. Cells outside the resulting area are removed.',
    category: 'multiplex',
    module: 'markerPositivity',
  },
  {
    id: 'marker-positivity-and-cell-rules',
    label: 'Cell typing & marker positivity',
    description:
      'Confirm mIF normalization, run the binary classifier on the mIF channels, and threshold those scores. RNA probe positivity uses spot detections that are already available. An RNA phenotype uses the cell-typing label when cell typing is included.',
    category: 'multiplex',
    module: 'markerPositivity',
  },
];

const workflowModuleLabels: Record<keyof StudyWorkflowModules, string> = {
  rnaProbes: 'RNA probes',
  markerPositivity: 'Marker positivity',
  cellRulesBasedCellTyping: 'Cell rules-based cell typing',
};

export const getConfiguredStudyWorkflowModules = ({
  modules,
}: {
  modules?: StudyWorkflowModules;
}): Array<{ key: keyof StudyWorkflowModules; label: string }> => {
  const configuration = getStudyWorkflowConfiguration(modules);

  return (Object.keys(workflowModuleLabels) as Array<keyof StudyWorkflowModules>)
    .filter((module) => configuration[module])
    .map((key) => ({ key, label: workflowModuleLabels[key] }));
};

export const getConfiguredMultiplexWorkflowSteps = (modules?: StudyWorkflowModules): StudyWorkflowStepDefinition[] => {
  const configuration = getStudyWorkflowConfiguration(modules);
  const configuredSteps = multiplexWorkflowSteps.filter((step) => !step.module || configuration[step.module]);

  return configuredSteps.length
    ? [markerAssignmentsWorkflowStep, markerNormalizationWorkflowStep, ...configuredSteps]
    : [];
};

// RNA probe steps come from the saved RNA probes setting, not from slides already being present.
// Marker positivity stays on the multiplex slide path until that workflow is settings-driven too.
export const getRnaProbeWorkflowSteps = (modules?: StudyWorkflowModules): StudyWorkflowStepDefinition[] => {
  if (!getStudyWorkflowConfiguration(modules).rnaProbes) {
    return [];
  }

  const rnaProbeStep = multiplexWorkflowSteps.find((step) => step.module === 'rnaProbes');

  return rnaProbeStep ? [markerAssignmentsWorkflowStep, markerNormalizationWorkflowStep, rnaProbeStep] : [];
};
